import React, { useState } from 'react';
import moment from 'moment';
import './CancellationPoliciesComponent.scss';
import { useTranslation } from 'react-i18next';
import HTMLReactParser from 'html-react-parser';
import { useAppConfig } from '../../Contexts/AppConfigContext';
import { CancellationPolicy, PaymentTypeEnum } from '../../models/BookingData';
import { DownOutlined, StopOutlined, UpOutlined } from '@ant-design/icons';

interface CancellationPoliciesComponentProps {
  cancellationPolicies: CancellationPolicy[];
  currencyCode: string;
  checkIn?: Date | string;
}

const CancellationPoliciesComponent = (props: CancellationPoliciesComponentProps): JSX.Element => {
  const appConfig = useAppConfig();
  const { t } = useTranslation();
  const { cancellationPolicies, currencyCode, checkIn } = props;
  const [visible, setVisible] = useState<boolean>(false);
  const now = moment();

  // Orden descendente: el deadline más tardío (más caro) arriba
  const sorted = [...cancellationPolicies].sort((a, b) => {
    if (!a.deadline) return 1;
    if (!b.deadline) return -1;
    return moment(b.deadline).valueOf() - moment(a.deadline).valueOf();
  });

  const fmt = `${appConfig.config.dateFormat} HH:mm`;

  const getCancellationInfo = (
    policy: CancellationPolicy,
    index: number
  ) => {
    // from = el propio deadline de esta política (hora exacta de la API)
    // to   = el deadline de la fila anterior (hora exacta), o el check-in si es la primera
    const from = moment(policy.deadline);
    const to   = index === 0
      ? (checkIn ? moment(checkIn) : null)
      : moment(sorted[index - 1].deadline);

    const fromFormatted = from.format(fmt);
    const toFormatted   = to ? to.format(fmt) : null;

    const isFree = policy.penalty === 0;
    // El porcentaje se deja como viene (un 10% no se escribe 10.00%); el importe sí lleva dos decimales.
    const penaltyText = policy.penaltyType === PaymentTypeEnum.percent
      ? `${policy.penalty}%`
      : `${policy.penalty?.toFixed(2)} ${currencyCode}`;

    const isFirst  = index === 0;
    const isPast   = to !== null && now.isAfter(to);
    const isActive = !isPast && now.isSameOrAfter(from);

    // El primero (más caro) siempre en naranja; el resto según estado
    let rowClass = 'cp-row';
    if (isFirst)        rowClass += ' cp-row--active';
    else if (isPast)    rowClass += ' cp-row--past';
    else if (isActive)  rowClass += isFree ? ' cp-row--active-free' : ' cp-row--active';
    else                rowClass += ' cp-row--future';

    let penaltyClass = 'cp-penalty';
    if (isFirst)        penaltyClass += ' cp-penalty--active';
    else if (isFree)    penaltyClass += isPast ? ' cp-penalty--muted' : ' cp-penalty--free';
    else if (isActive)  penaltyClass += ' cp-penalty--active';
    else if (isPast)    penaltyClass += ' cp-penalty--muted';
    else                penaltyClass += ' cp-penalty--future';

    return (
      <div key={index} className={rowClass}>
        <div className="cp-row-left">
          {isActive && !isFirst && <span className="cp-active-dot" />}
          <div className="cp-row-dates">
            <span className="cp-date-from">
              {HTMLReactParser(t('pages.booking-detail.cancellation-policy.from', [fromFormatted]))}
            </span>
            {!isFirst && (
              <>
                <span className="cp-date-sep">→</span>
                <span className="cp-date-to">
                  {toFormatted
                    ? HTMLReactParser(t('pages.booking-detail.cancellation-policy.to', [toFormatted]))
                    : t('pages.booking-detail.cancellation-policy.to-checkin')}
                </span>
              </>
            )}
          </div>
        </div>
        <span className={penaltyClass}>
          {isFree ? t('pages.booking-detail.extra-services.free') : penaltyText}
        </span>
      </div>
    );
  };

  return (
    <div className="cancellation-policies-component">
      <button type="button" className="cp-toggle" onClick={() => setVisible(v => !v)}>
        <span className="cp-toggle-label">
          <StopOutlined className="cp-icon" />
          {t('pages.booking-detail.cancellation-policies')}
          <span className="cp-count">{sorted.length}</span>
        </span>
        {visible ? <UpOutlined className="cp-chevron" /> : <DownOutlined className="cp-chevron" />}
      </button>

      {visible && (
        <div className="cp-list">
          {sorted.map((policy, index) =>
            getCancellationInfo(policy, index)
          )}
        </div>
      )}
    </div>
  );
};

export default CancellationPoliciesComponent;
